import React, { useState, useEffect, useCallback } from 'react'
import { Modal, LoadingIcon } from '@peracto/peracto-ui'
import startCase from 'lodash/startCase'
import { GET_LIST, useClient } from '@peracto/client'
import styled from 'styled-components'
import { OrderAddress } from '../index'
import { ModalContent } from '../styled'
import isEmpty from 'lodash/isEmpty'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'

import AddressForm from './AddressForm'

const AddressContainer = styled.div`
    border: 1px solid;
    border-color: #c8c8c8;
    border-radius: 3px;
    padding: 15px;
    cursor: pointer;
    margin-bottom: 30px;

    &.selected {
        background-color: #dfdfdf;
    }
`

const formatAddress = (address, user) => {
    // Because we have many ways of naming addresses
    const formattedAddress = {
        surname: address?.address?.lastName || address?.lastName || user.profile.last_name || ' ',
        forename:
            address?.address?.firstName || address?.firstName || user.profile.first_name || ' ',
        line1: address?.address?.addressLine1 || address?.addressLine1 || address?.address1 || ' ',
        line2: address?.address?.addressLine2 || address?.addressLine2 || address?.address2 || ' ',
        line3: address?.address?.addressLine3 || address?.addressLine3 || address?.address3 || ' ',
        line4: address?.address?.addressLine4 || address?.addressLine4 || address?.address4 || ' ',
        city: address?.address?.city || address?.city || ' ',
        county: address?.address?.county || address?.county || ' ',
        country: address?.address?.country || address?.country || ' ',
        postalCode: address?.address?.postcode || address?.postcode || ' ',
    }

    return formattedAddress
}

export const emptyAddress = {
    surname: '',
    forename: '',
    line1: '',
    line2: '',
    line3: '',
    line4: '',
    city: '',
    county: '',
    country: '',
    postalCode: '',
}

const initialAddress = {
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    addressLine4: '',
    city: '',
    county: '',
    country: '',
    postcode: '',
}

const AddressModal = ({ addressModal, setAddressModal, onUpdateBasket, user, addressData }) => {
    const [addresses, setAddresses] = useState([])
    const [showAddressForm, setShowAddressForm] = useState(false)
    const [selectedAddress, setSelectedAddress] = useState()
    const [isLoading, setIsLoading] = useState(true)
    const { client } = useClient()

    const fetchAddresses = useCallback(async () => {
        if (!addressModal) {
            return
        }
        if (isEmpty(user)) {
            setShowAddressForm(true)
            return
        }
        setIsLoading(true)
        try {
            const { data } = await client(GET_LIST, 'addresses', {
                id: `addresses?user=${user['@id']}`,
                filter: {
                    user: user['@id'],
                },
            })

            const filteredAddresses = data.filter(address => address.type.includes(addressModal))
            setAddresses(filteredAddresses)

            if (filteredAddresses.length === 0) {
                setShowAddressForm(true)
            }
        } catch (e) {
            console.error(e)
        } finally {
            setIsLoading(false)
        }
    }, [addressModal, client, user])

    useEffect(() => {
        setShowAddressForm(false)
        setSelectedAddress(initialAddress)
        fetchAddresses()

        if (!!addressModal === false) {
            setSelectedAddress(null)
        }
    }, [addressModal, fetchAddresses])

    useEffect(() => {
        return () => {
            setAddressModal(null)
        }
    }, [setAddressModal])

    return (
        <Modal
            className="modal-lg"
            bodyClassName="overflow-visible"
            isVisible={!!addressModal}
            title={`Select ${startCase(addressModal)} Address`}
            close={() => setAddressModal(false)}
            buttons={[
                {
                    type: 'btn-outline-secondary',
                    text: 'Cancel',
                    action: () => {
                        setAddressModal(false)
                    },
                },
                {
                    type: 'btn-success',
                    text: 'Save',
                    disabled: !!selectedAddress === false,
                    action: async () => {
                        const formattedAddress = formatAddress(selectedAddress, user)
                        const addressKey =
                            addressModal === 'delivery' ? 'deliveryAddress' : 'billingAddress'
                        const data = {
                            [addressKey]: formattedAddress,
                        }

                        if (addressModal === 'delivery') {
                            data.shipping = { service: null }
                        }

                        await onUpdateBasket(data)
                        setAddressModal(false)
                    },
                },
            ]}
        >
            <ModalContent
                className="!overflow-auto"
                {...(showAddressForm && {
                    style: {
                        minHeight: '340px',
                    },
                })}
            >
                <div className="row !w-full !text-left !m-0">
                    {showAddressForm ? (
                        <>
                            {!isEmpty(user) && addresses?.length > 0 && (
                                <button
                                    className="btn btn-link !mb-2 !px-0"
                                    onClick={e => setShowAddressForm(false)}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} className="!mr-2" />
                                    Select Address
                                </button>
                            )}
                            <AddressForm
                                selectedAddress={selectedAddress}
                                setSelectedAddress={setSelectedAddress}
                                type={addressModal}
                                addressData={addressData}
                            />
                        </>
                    ) : (
                        <>
                            {isLoading ? (
                                <div className="!flex !grow !items-center !flex-col !justify-center !h-full">
                                    <LoadingIcon />
                                </div>
                            ) : (
                                <>
                                    {addresses?.length > 0 ? (
                                        <>
                                            {addresses?.map(address => (
                                                <div
                                                    className="col-12 col-md-6"
                                                    key={address['@id']}
                                                >
                                                    <AddressContainer
                                                        className={
                                                            selectedAddress &&
                                                            selectedAddress['@id'] ===
                                                                address['@id']
                                                                ? 'selected'
                                                                : ''
                                                        }
                                                        onClick={() => {
                                                            setSelectedAddress(address)
                                                        }}
                                                    >
                                                        <OrderAddress address={address} />
                                                    </AddressContainer>
                                                </div>
                                            ))}
                                        </>
                                    ) : (
                                        <div className="col-12 !mt-6 !text-center">
                                            <p className="!w-full">
                                                No addresses available of this type for this user.
                                            </p>
                                        </div>
                                    )}

                                    <div className="col-12 !mb-6 !text-center">
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={() => setShowAddressForm(true)}
                                        >
                                            <FontAwesomeIcon
                                                icon={faPlusCircle}
                                                className="!mr-2"
                                            />
                                            Manually Add Address
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </ModalContent>
        </Modal>
    )
}

export default AddressModal
